if (!customElements.get('zoria-community-stories')) {
  customElements.define(
    'zoria-community-stories',
    class extends HTMLElement {
      constructor() {
        super();
        this.handleClick = this.handleClick.bind(this);
        this.handleScroll = this.handleScroll.bind(this);
      }

      connectedCallback() {
        this.addEventListener('click', this.handleClick);
        this.addEventListener('scroll', this.handleScroll, { passive: true });
        this.resizeObserver = new ResizeObserver(() => this.syncFocusableCards());
        this.resizeObserver.observe(this);
        this.querySelectorAll('[data-story-card]').forEach((card) => {
          this.syncSoundControl(card, null);
          this.syncPlaybackControl(card, null);
        });
        this.posterObserver = new IntersectionObserver(
          (entries, observer) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              const video = entry.target;
              if (!video.poster && video.dataset.storyVideoPoster) {
                video.poster = video.dataset.storyVideoPoster;
              }
              if (video.closest('[data-story-slide]')?.classList.contains('is-active')) {
                this.activateVideo(video);
                video.play()?.catch(() => {});
                this.syncSoundControl(video.closest('[data-story-card]'), video);
                this.syncPlaybackControl(video.closest('[data-story-card]'), video);
              }
              observer.unobserve(video);
            });
          },
          { rootMargin: '300px 0px' },
        );
        this.querySelectorAll('[data-story-video]').forEach((video) => this.posterObserver.observe(video));
        this.syncFocusableCards();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
        this.removeEventListener('scroll', this.handleScroll);
        cancelAnimationFrame(this.scrollFrame);
        this.resizeObserver?.disconnect();
        this.posterObserver?.disconnect();
      }

      handleScroll() {
        cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = requestAnimationFrame(() => this.syncFocusableCards());
      }

      handleClick(event) {
        const playbackControl = event.target.closest('[data-story-play-pause]');
        if (playbackControl && this.contains(playbackControl)) {
          const card = playbackControl.closest('[data-story-card]');
          const video = card.querySelector('[data-story-slide].is-active video');
          if (!video) return;
          this.activateVideo(video);
          if (video.paused) video.play()?.catch(() => {});
          else video.pause();
          this.syncPlaybackControl(card, video);
          return;
        }

        const soundControl = event.target.closest('[data-story-sound]');
        if (soundControl && this.contains(soundControl)) {
          const card = soundControl.closest('[data-story-card]');
          const video = card.querySelector('[data-story-slide].is-active video');
          if (!video) return;
          this.activateVideo(video);
          video.muted = !video.muted;
          this.syncSoundControl(card, video);
          return;
        }

        const control = event.target.closest('[data-story-previous], [data-story-next]');
        if (!control || !this.contains(control)) return;
        const card = control.closest('[data-story-card]');
        const slides = card.querySelectorAll('[data-story-slide]');
        const direction = control.hasAttribute('data-story-next') ? 1 : -1;
        const index = (Number(card.dataset.activeSlide) + direction + slides.length) % slides.length;
        card.dataset.activeSlide = index;
        slides.forEach((slide, slideIndex) => {
          const active = slideIndex === index;
          slide.classList.toggle('is-active', active);
          slide.setAttribute('aria-hidden', String(!active));
        });
        card.querySelectorAll('[data-story-progress]').forEach((item, itemIndex) => {
          item.classList.toggle('is-active', itemIndex === index);
          item.classList.toggle('is-viewed', itemIndex < index);
        });
        this.syncMedia(card, true);
      }

      activateVideo(video) {
        if (!video) return;
        if (!video.poster && video.dataset.storyVideoPoster) video.poster = video.dataset.storyVideoPoster;
        if (video.dataset.loaded === 'true') return;
        const source = video.dataset.storyVideoSrc;
        if (!source) return;
        video.src = source;
        video.dataset.loaded = 'true';
        if (video.dataset.playbackEventsReady !== 'true') {
          video.dataset.playbackEventsReady = 'true';
          ['play', 'pause', 'ended', 'loadeddata'].forEach((eventName) => {
            video.addEventListener(eventName, () => this.syncPlaybackControl(video.closest('[data-story-card]'), video));
          });
        }
        video.load();
      }

      syncMedia(card, allowLoad = false) {
        const slides = card.querySelectorAll('[data-story-slide]');
        const activeIndex = Number(card.dataset.activeSlide);
        slides.forEach((slide, index) => {
          const video = slide.querySelector('video');
          if (!video) return;
          if (index === activeIndex) {
            if (allowLoad) this.activateVideo(video);
            if (video.dataset.loaded === 'true') video.play()?.catch(() => {});
          } else {
            video.pause();
            video.muted = true;
          }
        });
        this.syncSoundControl(card, slides[activeIndex]?.querySelector('video'));
        this.syncPlaybackControl(card, slides[activeIndex]?.querySelector('video'));
      }

      syncSoundControl(card, video) {
        const control = card.querySelector('[data-story-sound]');
        control.hidden = !video || video.dataset.loaded !== 'true';
        if (control.hidden) return;
        control.setAttribute('aria-pressed', String(!video.muted));
        control.classList.toggle('is-audible', !video.muted);
        control.setAttribute('aria-label', video.muted ? 'הפעלת שמע בווידאו' : 'השתקת שמע בווידאו');
      }

      syncPlaybackControl(card, video) {
        if (!card) return;
        const control = card.querySelector('[data-story-play-pause]');
        if (!control) return;
        control.hidden = !video || video.dataset.loaded !== 'true';
        if (control.hidden) return;
        const paused = video.paused;
        control.setAttribute('aria-pressed', String(paused));
        control.classList.toggle('is-paused', paused);
        control.setAttribute('aria-label', paused ? 'הפעלת הסרטון' : 'השהיית הסרטון');
      }

      syncFocusableCards() {
        if (window.ZoriaCarouselFocus) {
          window.ZoriaCarouselFocus.sync(this);
          return;
        }
        const carouselRect = this.getBoundingClientRect();
        this.querySelectorAll('[data-story-card]').forEach((card) => {
          const rect = card.getBoundingClientRect();
          const offscreen = rect.right <= carouselRect.left + 1 || rect.left >= carouselRect.right - 1;
          card.inert = offscreen;
          if (offscreen) {
            card.dataset.zoriaOffscreen = 'true';
            card.setAttribute('aria-hidden', 'true');
          } else if (card.dataset.zoriaOffscreen === 'true') {
            delete card.dataset.zoriaOffscreen;
            card.removeAttribute('aria-hidden');
          }
        });
      }
    },
  );
}
